import React from "react";
import { t } from "ttag";

import { templates } from "@reactivated";

import { PageSkeleton } from "../components/PageSkeleton";

type Entry = templates.SiteMapDocumentTemplate["entries"][number];

interface SiteMapNode {
    title: string;
    url: string;
    children: SiteMapNode[];
}

// Rebuild the flat, level-tagged page list (treebeard annotated order) into a
// nested tree so React can emit balanced <ul>/<li> markup.
// Exported for the level-jump unit test; not part of the template contract.
export const buildTree = (entries: readonly Entry[]): SiteMapNode[] => {
    const roots: SiteMapNode[] = [];
    const stack: SiteMapNode[] = [];
    for (const entry of entries) {
        const node: SiteMapNode = {
            title: entry.title,
            url: entry.url,
            children: [],
        };
        // `depth` is 0-based (info["level"] = treebeard depth-minus-start_depth);
        // trim the stack back to this node's parent depth before re-pushing.
        // Levels can jump by MORE than +1: the annotated list runs over a
        // FILTERED queryset, so a hidden/not-live parent with a visible
        // descendant yields e.g. level 0 -> level 2, leaving a hole at the
        // parent slot after the resize. The guard treats the missing parent as
        // "no parent" and roots the orphan -- this must never throw: an
        // exception here is an SSR 500.
        stack.length = entry.depth;
        // .at(-1) rather than [length - 1]: after a hole-creating resize the
        // slot is empty, and .at is typed `SiteMapNode | undefined` so the
        // guard below type-checks honestly.
        const parent = stack.at(-1);
        if (parent) {
            parent.children.push(node);
        } else {
            roots.push(node);
        }
        stack.push(node);
    }
    return roots;
};

const SiteMapList = ({
    nodes,
}: {
    nodes: readonly SiteMapNode[];
}): React.JSX.Element => (
    <ul>
        {nodes.map((node, i) => (
            <li key={i}>
                <a href={node.url}>{node.title}</a>
                {node.children.length > 0 && (
                    <SiteMapList nodes={node.children} />
                )}
            </li>
        ))}
    </ul>
);

export const Template = (
    props: templates.SiteMapDocumentTemplate,
): React.JSX.Element => {
    const tree = buildTree(props.entries);
    return (
        <PageSkeleton pageChrome={props.page_chrome}>
            <div className="l-capped-width sitemap">
                <div>
                    <h2>{t`Sitemap`}</h2>
                </div>
                <div className="sitemap__links">
                    <SiteMapList nodes={tree} />
                </div>
            </div>
        </PageSkeleton>
    );
};
